import {
  formatZonedTime,
  selectCurrentUser,
  useLocale,
  zoneAbbreviation,
} from '@saniplus/api-client';
import {
  AppShellWorker,
  Calendar,
  ChartColumn,
  LogIn,
  User,
  type WorkerNavItem,
} from '@saniplus/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router';

import { HOME_PATH, HOURS_PATH, IDB_NAME, PROFILE_PATH, SHIFTS_PATH } from '../app/config';
import { useAppSelector } from '../app/hooks';
import { selectPendingCount, selectQueueState, useGetMeContextQuery } from '../features/offline';
import { siteTimezoneOrNull } from '../features/punch/lib/format';
import { UpdatePrompt } from '../pwa/UpdatePrompt';

/**
 * The authenticated shell, on every screen behind the PIN gate.
 *
 * Its three invariants come straight from the page judgements: the rail is
 * wordmark + site clock, the connectivity band sits directly beneath it and
 * pushes content down, and the bottom nav is four labelled tabs — never
 * icon-only. The FR/EN toggle is deliberately **not** here: it lives on the
 * login screen (before auth) and on Profil, and one setting with two owners is
 * a state-sync bug waiting to be written.
 *
 * The clock is the SITE's, not the phone's. A worker in Blanc-Sablon and a
 * worker in Saint-Esprit are on different wall clocks, and every time this
 * product shows is the one their shift is measured against.
 */
export function AppLayout() {
  const { t } = useTranslation();
  const { locale } = useLocale({ dbName: IDB_NAME });
  const location = useLocation();
  const queue = useAppSelector(selectQueueState);
  const pending = useAppSelector(selectPendingCount);
  const user = useAppSelector(selectCurrentUser);

  // One cache entry, shared with the punch screen: `punchApi.getMeContext`
  // provides `Context`, so this is not a second request.
  const { data: context } = useGetMeContextQuery();

  // No clock rather than a wrong one, in both directions: `undefined` is "the
  // context has not arrived yet", and `siteTimezoneOrNull` answers `null` when
  // it has arrived but names no site. A Montréal clock over a plant we cannot
  // name is a plausible-but-wrong value, which is worse than a blank.
  const zone = siteTimezoneOrNull(context);

  const clock = useSiteClock(zone, locale);

  const nav: WorkerNavItem[] = [
    { key: 'punch', href: HOME_PATH, label: t('nav.punch'), icon: LogIn },
    { key: 'shifts', href: SHIFTS_PATH, label: t('nav.shifts'), icon: Calendar },
    { key: 'hours', href: HOURS_PATH, label: t('nav.hours'), icon: ChartColumn },
    { key: 'profile', href: PROFILE_PATH, label: t('nav.profile'), icon: User },
  ].map((item) => ({
    ...item,
    current:
      item.href === HOME_PATH
        ? location.pathname === HOME_PATH
        : location.pathname.startsWith(item.href),
  }));

  return (
    <AppShellWorker
      /*
       * NO NAV-HEIGHT PADDING UNDER `main` (removed 2026-09-03).
       *
       * The P0-2 fix added `[&>main]:pb-[calc(var(--spacing-nav)+safe)]`
       * against content bleeding under the bottom nav. `AppShellWorker` is
       * `flex h-dvh flex-col overflow-hidden` with `main` as `min-h-0 flex-1`
       * and the nav as a `shrink-0` SIBLING below it: `main` cannot reach the
       * nav's box, so that padding was 68 px of dead canvas at the bottom of
       * every tab. On the punch screen — whose column is `h-full` of `main`'s
       * content box — it pushed the whole state/key/supervisor stack up and
       * left the empty block a worker reported under « Contacter mon
       * superviseur », while costing the punch screen exactly the height the
       * tasks summary needed. The nav still carries its own
       * `pb-safe-bottom`, so a notched phone is unaffected.
       *
       * The landscape rail clause stays: below 480 px of viewport height the
       * rail must collapse to 48 px (DESIGN.md, "Worker PWA in landscape"),
       * and it was rendering at ~96 because the safe-area top padding was
       * added on top of the full 64 px rail.
       */
      className={'sideways:[&>header]:min-h-rail-short sideways:[&>header]:pt-0'}
      brandLabel={t('app.brand')}
      {...(clock === null ? {} : { clock })}
      online={queue.online}
      queueCount={pending}
      connectivityLabels={{
        state: queue.online ? t('connectivity.online') : t('connectivity.offline'),
        queue: t('connectivity.queue', { count: pending }),
        clear: t('connectivity.clear'),
      }}
      /*
       * The « Mise à jour prête » band lives in the shell, not on Profil.
       *
       * A waiting build is only ever released by « Recharger » (or by every
       * tab closing) — `registerType: 'prompt'` never reloads on its own. When
       * the band was mounted on Profil alone, a worker on the punch screen was
       * never told a build was waiting, so a reload kept serving the old shell
       * and a deploy looked like it had not happened.
       */
      banner={<UpdatePrompt />}
      nav={nav}
      navLabel={t('nav.label')}
      renderNavLink={(item, className, children) => (
        <NavLink
          to={item.href}
          end={item.href === HOME_PATH}
          className={className}
          aria-current={item.current === true ? 'page' : undefined}
        >
          {children}
        </NavLink>
      )}
      skipLabel={t('app.skipToContent')}
    >
      {/*
        * Whose phone this is, announced once per screen and drawn nowhere.
        *
        * The rail carries the wordmark and the site clock — there is no room
        * for a name, and a worker holding their own phone does not need one.
        * A screen reader landing in the shell does. It lives in the shell
        * rather than on the home screen so the answer is the same on all four
        * tabs. `data-testid="home-greeting"` is load-bearing: it is the
        * canonical hook for asserting "this person is signed in" from a test.
        */}
      {user === null ? null : (
        <p data-testid="home-greeting" className="sr-only">
          {t('home.greeting', { name: `${user.firstName} ${user.lastName}` })}
        </p>
      )}
      <Outlet />
    </AppShellWorker>
  );
}

/**
 * The rail clock, re-rendered on the minute rather than the second.
 *
 * A ticking seconds display in a header is motion in the corner of the eye all
 * night; the minute is the unit every shift window is written in anyway.
 */
function useSiteClock(
  zone: string | null,
  locale: 'fr' | 'en',
): { time: string; zone: string } | null {
  const [nowIso, setNowIso] = useState(() => new Date().toISOString());

  useEffect(() => {
    const id = setInterval(() => setNowIso(new Date().toISOString()), 30_000);
    return () => clearInterval(id);
  }, []);

  if (zone === null) return null;
  return { time: formatZonedTime(nowIso, zone, locale), zone: zoneAbbreviation(nowIso, zone, locale) };
}
